import { ToastProvider, useToast } from '@syncanix/ui';
import { useEffect } from 'react';

import { setNetworkErrorToastHandler } from '@/api/error-toast';

import { useT } from './I18nProvider';

import type { PropsWithChildren, ReactElement } from 'react';

/**
 * Installs the localized network-failure toast as the global handler the
 * Apollo error link notifies (`api/error-toast.ts`). Mounted inside the
 * ToastProvider below so `useToast` resolves the real region.
 */
function NetworkErrorToastInstaller({ children }: PropsWithChildren): ReactElement {
  const toast = useToast();
  const { t } = useT();
  useEffect(() => {
    setNetworkErrorToastHandler(() => {
      toast.error(t('error.network.toast'));
    });
    return () => {
      setNetworkErrorToastHandler(null);
    };
  }, [toast, t]);
  return <>{children}</>;
}

/**
 * The app-root toast region (A18 slice 4e — the toast promoted from the docs
 * feature to `@syncanix/ui`). Mount ONCE, inside `I18nProvider` (localized
 * dismiss label) and outside the page `ErrorBoundary` (the region survives a
 * page render failure). Also wires the Apollo non-401 network-error toast.
 */
export function AppToastProvider({ children }: PropsWithChildren): ReactElement {
  const { t } = useT();
  return (
    <ToastProvider dismissLabel={t('toast.dismiss')}>
      <NetworkErrorToastInstaller>{children}</NetworkErrorToastInstaller>
    </ToastProvider>
  );
}
